import type { App } from 'vue'
import { inject } from 'vue'
import { useBlockLibraryCustomRolePermissions } from '@/composables/useBlockLibraryCustomRolePermissions'
import { useComponents } from '@/store/modules/App/component'
import { useSpace } from '@/store/modules/App/space'
import { useAssets } from 'src/store/modules/App/asset'
import { Permissions } from '@/types'
import { generateTree, getRootFolder } from '@utils/specifics/index'

export type PermissionsPlugin = {
  can: (permission: Permissions, option?: string | number) => boolean
  canNot: (permission: Permissions) => boolean
}

const permissionsPluginInjectionKey = Symbol()

/*
 * This plugin has a unit test in this path: src/utils/helpers/__tests__/permissionsHelper.test.ts
 * With the help of the test it is easier to understand how the plugin flow works
 *
 * The types are defined in src/types/global.d.ts, avoid overriding them here
 */
export default {
  install: function (app: App): void {
    app.config.globalProperties.$can = function (permission, option): boolean {
      const spaceStore = useSpace()
      const assetsStore = useAssets()
      const componentsStore = useComponents()

      const isOptionDifferentFromNullAndUndefined =
        option !== undefined && option !== null
      const isOptionNullOrUndefined = option === undefined || option === null

      // we should consider to refactor this function using spaceStore.userPermissions instead of spaceStore.spaceRoles
      // since the userPermissions is a more accessible object with all the permissions of a user from all the role it may have
      // spaceStore.spaceRoles, instead, it's not tested against a user that has multiple roles
      // and therefore there could be misleadings in the permissions validation
      // in the case that a role has a rule that should override another rule
      const userPermissions = spaceStore.userPermissions

      /**
       * When we know it's a user admin and has all permissions, we can exit and directly return true
       */
      if (userPermissions.permissions.includes(Permissions.ActAsAdmin)) {
        return true
      }

      // @TODO: we need to check why here 👇 we validate the Permissions.All (therefore admin?) permission against EditDataSourceKeys
      // and also, if Permissions.All is an admin, couldn't we use only Permissions.ActAsAdmin in place and remove 'all'?
      if (
        userPermissions.permissions.includes(Permissions.All) &&
        permission !== Permissions.EditDataSourceKeys &&
        permission !== Permissions.ApplyToBlockSubfolders // This permission should be checked explicitly, not being automatically granted with "all" aka admin
      ) {
        return true
      }

      // in the future all controls should be moved to this switch and based on spaceStore.userPermissions
      // instead of a filtered result from spaceStore.spaceRoles
      switch (permission) {
        case Permissions.EditLanguages: {
          const isAllowedAndBlockedLanguagesEmpty =
            userPermissions.allowed_languages.length === 0 &&
            userPermissions.blocked_languages.length === 0

          if (isOptionNullOrUndefined || isAllowedAndBlockedLanguagesEmpty) {
            return true
          }

          if (
            isOptionDifferentFromNullAndUndefined &&
            userPermissions.blocked_languages.length > 0
          ) {
            return !userPermissions.blocked_languages.includes(
              option.toString(),
            )
          }

          return userPermissions.allowed_languages.includes(option.toString())
        }
        case Permissions.ManageAssetFolders: {
          const folderIds = spaceStore.spaceRoles.reduce(
            (
              acc: {
                allowed: number[]
                blocked: number[]
              },
              rule,
            ) => {
              if (rule.asset_folder_ids.length > 0) {
                acc.allowed.push(...rule.asset_folder_ids)
              }
              if (rule.blocked_asset_folder_ids.length > 0) {
                acc.blocked.push(...rule.blocked_asset_folder_ids)
              }
              return acc
            },
            { allowed: [], blocked: [] },
          )

          if (
            isOptionNullOrUndefined &&
            !folderIds.allowed.length &&
            !folderIds.blocked.length
          ) {
            return true
          }

          const noAllowedFolders = folderIds.allowed.length === 0
          const canAccessFolder = folderIds.allowed.includes(Number(option))
          const cantAccessFolder = folderIds.blocked.includes(Number(option))

          // Allow if no allowed list and folder isn't blocked.
          if (noAllowedFolders && !cantAccessFolder) {
            return true
          }

          // Deny if folder is both allowed and blocked (blocked takes priority).
          if (canAccessFolder && cantAccessFolder) {
            return false
          }

          // Allow if folder is allowed and not blocked.
          if (canAccessFolder && !cantAccessFolder) {
            return true
          }

          // Deny if folder is blocked and not allowed.
          if (!canAccessFolder && cantAccessFolder) {
            return false
          }

          const requiredPath = getRootFolder(
            Number(option),
            // @ts-expect-error resolve the type error later
            generateTree(assetsStore.folders),
          )

          return (
            requiredPath !== null && folderIds.allowed.includes(requiredPath.id)
          )
        }
      }

      const permissions = spaceStore.spaceRoles.filter((rule) => {
        switch (permission) {
          case Permissions.ViewField: {
            const hiddenRegex = new RegExp(
              `(${rule.field_permissions.join('|')})`,
            )
            if (
              isOptionDifferentFromNullAndUndefined &&
              hiddenRegex.test(option.toString()) &&
              rule.field_permissions &&
              rule.field_permissions.length > 0
            ) {
              return false
            }

            const visibleRegex = new RegExp(
              `(${rule.allowed_field_permissions.join('|')})`,
            )

            if (
              isOptionDifferentFromNullAndUndefined &&
              visibleRegex.test(option.toString()) &&
              rule.allowed_field_permissions.length > 0 &&
              rule.field_permissions.length === 0
            ) {
              return true
            }

            if (
              isOptionDifferentFromNullAndUndefined &&
              rule.field_permissions.length === 0 &&
              rule.allowed_field_permissions.length > 0 &&
              !visibleRegex.test(option.toString())
            ) {
              return false
            }

            return true
          }

          case Permissions.EditField: {
            return (
              isOptionNullOrUndefined ||
              !rule.readonly_field_permissions.includes(option?.toString())
            )
          }

          case Permissions.AddComponent: {
            const component = componentsStore.getComponents.find(
              (c) => c.id === Number(option),
            )

            if (rule.component_group_uuids?.length > 0) {
              if (
                component?.component_group_uuid &&
                rule.component_group_uuids.includes(
                  component?.component_group_uuid,
                )
              ) {
                return true
              }

              const mappedUuids = new Set(
                componentsStore.getComponentGroups.map((group) => group.uuid),
              )

              if (!mappedUuids.has(component?.component_group_uuid || '')) {
                return false
              }

              return false
            }

            if (rule.blocked_component_group_uuids?.length > 0) {
              if (
                component?.component_group_uuid &&
                rule.blocked_component_group_uuids.includes(
                  component.component_group_uuid,
                )
              ) {
                return false
              }

              return true
            }

            if (
              rule.allowed_component_ids.length === 0 &&
              !rule.component_ids.includes(Number(option))
            ) {
              return true
            }

            if (
              rule.allowed_component_ids.length > 0 &&
              rule.allowed_component_ids.includes(Number(option))
            ) {
              return true
            }

            return (
              isOptionNullOrUndefined ||
              (rule.allowed_component_ids.length === 0 &&
                !rule.component_ids.includes(Number(option)))
            )
          }

          case Permissions.EditDataSources: {
            if (
              isOptionDifferentFromNullAndUndefined &&
              rule.datasource_ids.length > 0 &&
              !rule.datasource_ids.includes(Number(option))
            ) {
              return false
            }

            if (
              isOptionDifferentFromNullAndUndefined &&
              rule.datasource_ids.length === 0 &&
              rule.blocked_datasource_ids.length > 0 &&
              rule.blocked_datasource_ids.includes(Number(option))
            ) {
              return false
            }

            return rule.permissions.includes(permission)
          }

          case Permissions.DeployStories: {
            if (!isOptionDifferentFromNullAndUndefined) {
              return rule.permissions.includes(permission)
            }

            const optionNumber = Number(option)

            if (
              rule.branch_ids.length > 0 &&
              !rule.branch_ids.includes(optionNumber)
            ) {
              return false
            }

            if (
              rule.blocked_branch_ids.length > 0 &&
              rule.blocked_branch_ids.includes(optionNumber)
            ) {
              return false
            }

            if (
              !rule.branch_ids.length &&
              rule.blocked_branch_ids.length > 0 &&
              !rule.blocked_branch_ids.includes(optionNumber)
            ) {
              return true
            }

            return rule.permissions.includes(permission)
          }

          case Permissions.ManageBlockLibrary: {
            return rule.permissions.includes(permission)
          }

          case Permissions.ManageBlock: {
            if (!rule.permissions.includes(Permissions.ManageBlockLibrary)) {
              return false
            }

            const { allowedComponents } = useBlockLibraryCustomRolePermissions()

            return allowedComponents.value.some(
              (component) => component.id === Number(option),
            )
          }
        }

        if (
          [
            Permissions.ExecuteTasks,
            Permissions.CreateTasks,
            Permissions.DeleteTasks,
            Permissions.EditTasks,
          ].includes(permission) &&
          rule.permissions.includes(Permissions.AccessTasks)
        ) {
          return true
        }

        if (isOptionNullOrUndefined && rule.permissions.includes(permission)) {
          return true
        }

        if (
          isOptionDifferentFromNullAndUndefined &&
          rule.permissions.includes(permission)
        ) {
          const storyPath = `${option}/`
          const isAllowedPath =
            rule.allowed_path && new RegExp(rule.allowed_path).test(storyPath)
          const isNotBlockedPath =
            rule.blocked_path && !new RegExp(rule.blocked_path).test(storyPath)
          const isAllowedPathAndBlockedPathEmpty =
            !rule.allowed_path && !rule.blocked_path

          if (
            isAllowedPathAndBlockedPathEmpty ||
            isAllowedPath ||
            isNotBlockedPath
          ) {
            return true
          }

          if (
            !rule.allowed_path?.length &&
            rule.blocked_paths.length > 0 &&
            rule.blocked_path &&
            new RegExp(rule.blocked_path).test(storyPath)
          ) {
            return false
          }
        }

        return false
      })

      return permissions.length > 0
    }

    app.config.globalProperties.$canNot = function (permission): boolean {
      const spaceStore = useSpace()

      const permissions = spaceStore.spaceRoles.filter((rule) => {
        if (rule.permissions.includes(Permissions.All)) {
          return false
        }

        return rule.permissions.includes(permission)
      })

      return permissions.length > 0
    }

    app.config.globalProperties.$canEditComponent = function (space): boolean {
      return !space.parent_id
    }

    app.provide(permissionsPluginInjectionKey, {
      can: app.config.globalProperties.$can.bind(app.config.globalProperties),
      canNot: app.config.globalProperties.$canNot.bind(
        app.config.globalProperties,
      ),
    })
  },
}

export function usePermissionsPlugin(): PermissionsPlugin {
  return inject(permissionsPluginInjectionKey, {
    can: () => false,
    canNot: () => true,
  })
}
